import { ethers } from "ethers";
import { NETWORKS } from "../../utils/config";
import CloseCarbonEnergyEfficiency from "../../Abi/CloseCarbonEnergyEfficiency.json";

const CHAIN_ID = 11155420; // Optimism Sepolia testnet

// Validate contract address exists in NETWORKS config
if (!NETWORKS[CHAIN_ID]?.CLOSE_CARBON_ENERGY_EFFICIENCY) {
  throw new Error(`Contract address for chain ID ${CHAIN_ID} is undefined in NETWORKS config`);
}
const CONTRACT_ADDRESS = NETWORKS[CHAIN_ID].CLOSE_CARBON_ENERGY_EFFICIENCY;

// Initialize contract instance with  error handling
const getContractInstance = async (providerOrSigner) => {
  if (!providerOrSigner) {
    throw new Error("Provider or signer is required");
  }
  
  try {
    return new ethers.Contract(
      CONTRACT_ADDRESS,
      CloseCarbonEnergyEfficiency.abi,
      providerOrSigner
    );
  } catch (error) {
    console.error("Failed to create contract instance:", error);
    throw new Error("Failed to initialize contract");
  }
};

// Set an energy goal for a facility
export const setEnergyGoal = async (targetReduction, signer) => {
  if (!signer) throw new Error("Signer is required");
  if (typeof targetReduction !== 'number' || targetReduction <= 0) {
    throw new Error("Target reduction must be a positive number");
  }

  try {
    const contract = await getContractInstance(signer);
    const convertedTargetReduction = ethers.toBigInt(targetReduction);

    // Gas estimation with  error handling
    let gasLimit;
    try {
      const estimatedGas = await contract.setEnergyGoal.estimateGas(convertedTargetReduction);
      gasLimit = (estimatedGas * 120n) / 100n; // 20% buffer
    } catch (estimateError) {
      console.warn("Gas estimation failed:", estimateError.message);
      gasLimit = 500000n; // Fallback gas limit
    }

    const tx = await contract.setEnergyGoal(convertedTargetReduction, { gasLimit });
    const receipt = await tx.wait();

    return {
      transactionHash: receipt.hash,
      blockNumber: receipt.blockNumber,
      gasUsed: receipt.gasUsed.toString(),
      status: receipt.status === 1 ? "success" : "failed",
    };
  } catch (error) {
    console.error("Error in setEnergyGoal:", error.message);
    throw new Error(`Failed to set energy goal: ${error.message}`);
  }
};

// Get earned credits per source for a specific goal
export const getEarnedCreditsPerSourceForGoal = async (facility, goalId, provider) => {
  if (!provider) throw new Error("Provider is required");
  if (!ethers.isAddress(facility)) throw new Error("Invalid facility address");
  if (typeof goalId !== 'number') throw new Error("Invalid goal ID");

  try {
    const contract = await getContractInstance(provider);
    const credits = await contract.getEarnedCreditsPerSourceForGoal(facility, goalId);
    return credits.map((credit) => ethers.formatUnits(credit));
  } catch (error) {
    console.error("Error in getEarnedCreditsPerSourceForGoal:", error.message);
    throw new Error(`Failed to get earned credits: ${error.message}`);
  }
};

// Get total earned credits for a facility
export const getTotalEarnedCredits = async (facility, provider) => {
  if (!provider) throw new Error("Provider is required");
  if (!ethers.isAddress(facility)) throw new Error("Invalid facility address");

  try {
    const contract = await getContractInstance(provider);
    const totalCredits = await contract.getTotalEarnedCredits(facility);
    // const decimals = await contract.decimals();

    return ethers.formatUnits(totalCredits);
  } catch (error) {
    console.error("Error in getTotalEarnedCredits:", error.message);
    throw new Error(`Failed to get total earned credits: ${error.message}`);
  }
};
